// Traffic control plan layout (Ryan, Oct 30 2026) — the MUTCD Part 6 numbers and where the signs and
// cones go along the road someone traced on the satellite. Plain arithmetic, no AI: the same
// distances a certified person works out by hand from the tables. The person still checks and signs.
//
// Distances are in FEET. The traced road runs in the direction traffic travels and ends where the
// work area starts; everything is measured back up that line from its end.

// Advance warning sign spacing (MUTCD Table 6C-1): A = transition to the first sign, B = first to
// second, C = second to third.
export const ROAD_TYPES = {
  urban_low:  { label: 'Urban street (40 mph or less)', A: 100,  B: 100,  C: 100 },
  urban_high: { label: 'Urban road (45 mph or more)',   A: 350,  B: 350,  C: 350 },
  rural:      { label: 'Rural road',                    A: 500,  B: 500,  C: 500 },
  freeway:    { label: 'Expressway / freeway',          A: 1000, B: 1500, C: 2640 },
};
// Longitudinal buffer space by speed (MUTCD Table 6C-2).
const BUFFER = { 20: 115, 25: 155, 30: 200, 35: 250, 40: 305, 45: 360, 50: 425, 55: 495, 60: 570, 65: 645, 70: 730, 75: 820 };
export const SPEEDS = Object.keys(BUFFER).map(Number);
export const SETUPS = {
  lane_closed: { label: 'One lane closed (multi-lane road)' },
  shoulder:    { label: 'Shoulder work' },
};

// Which standard the drawing is to (Ryan, Nov 2 2026): the drawing asks — ATSSA (the MUTCD Part 6
// typical applications an ATSSA-certified person works to) or the NDOT Standard Plans (its Temporary
// Traffic Control sheets). The distances are the same arithmetic either way; the print names the
// standard and the typical / sheet. Same words as tcpStandard() in server/api.js (keep in step).
export const STANDARDS = {
  atssa: { label: 'ATSSA', name: 'ATSSA — MUTCD Part 6 typical applications (2023 edition)', short: 'ATSSA / MUTCD Part 6' },
  ndot:  { label: 'NDOT Standard Plans', name: 'NDOT Standard Plans for Road and Bridge Construction (2024 edition), Temporary Traffic Control sheets', short: 'NDOT Standard Plans (2024)' },
};
// The reference for a plan: { key, label, name, short, ref, refDefault, needsRef } or null until picked.
export function tcpReference(plan) {
  const s = STANDARDS[plan && plan.standard];
  if (!s) return null;
  const typed = String((plan && plan.standard_ref) || '').trim();
  let refDefault = '';
  if (plan.standard === 'atssa') {
    const fwy = plan.road_type === 'freeway';
    refDefault = plan.setup === 'shoulder'
      ? (fwy ? 'Typical Application TA-5 — Shoulder Closure on a Freeway' : 'Typical Application TA-6 — Shoulder Work with Minor Encroachment')
      : (fwy ? 'Typical Application TA-33 — Stationary Lane Closure on a Divided Highway' : 'Typical Application TA-33 — Stationary Lane Closure on a Divided Highway (closest typical for one lane closed on a multi-lane street)');
  }
  return { key: plan.standard, ...s, ref: typed || refDefault || 'sheet number not entered', refDefault, needsRef: !typed && !refDefault };
}

const up5 = n => Math.ceil(n / 5) * 5;

// The numbers for a setup. W = how far the cones move traffic over: a lane width, or the shoulder.
export function tcpNumbers({ setup = 'lane_closed', speed = 45, roadType = 'urban_high', laneWidth = 12, shoulderWidth = 8 }) {
  const S = Number(speed) || 45;
  const W = setup === 'shoulder' ? (Number(shoulderWidth) || 8) : (Number(laneWidth) || 12);
  // Merging taper (Table 6C-4): L = W·S at 45 mph and over, W·S²/60 at 40 and under.
  const L = S >= 45 ? W * S : (W * S * S) / 60;
  const taper = up5(setup === 'shoulder' ? L / 3 : L);    // a shoulder taper is a third of L
  const road = ROAD_TYPES[roadType] || ROAD_TYPES.urban_high;
  return {
    speed: S, W, taper, buffer: BUFFER[S] || up5((S * S) / 7),
    A: road.A, B: road.B, C: road.C,
    coneTaper: S,          // channelizer spacing in a taper ≤ S feet
    coneTangent: 2 * S,    // and along the work area ≤ 2S feet
    termination: 100,      // downstream taper, 50–100 ft
  };
}

// The signs, nearest the work first (A from the transition, then +B, then +C).
export function signsFor(setup, side = 'right') {
  const R = side === 'left' ? 'LEFT' : 'RIGHT';
  if (setup === 'shoulder') return [
    { code: side === 'left' ? 'W21-5aL' : 'W21-5aR', text: `${R} SHOULDER CLOSED` },
    { code: 'W20-1', text: 'ROAD WORK AHEAD' },
  ];
  return [
    { code: side === 'left' ? 'W4-2L' : 'W4-2R', text: 'LANE ENDS (symbol)' },
    { code: side === 'left' ? 'W20-5L' : 'W20-5R', text: `${R} LANE CLOSED AHEAD` },
    { code: 'W20-1', text: 'ROAD WORK AHEAD' },
  ];
}

// ── Geometry: a flat local frame in feet around the end of the traced road ────────────────────────
const FT_PER_M = 3.28084;
function frame(origin) {
  const [lat0, lng0] = origin;
  const kx = Math.cos((lat0 * Math.PI) / 180) * 111320 * FT_PER_M, ky = 110540 * FT_PER_M;
  return {
    to: ([lat, lng]) => ({ x: (lng - lng0) * kx, y: (lat - lat0) * ky }),
    back: ({ x, y }) => [lat0 + y / ky, lng0 + x / kx],
  };
}
const len = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

// A point `d` feet back up the traced road from its end (negative d = on past the end, downstream),
// with the direction of travel there. Past either end the line carries straight on.
function along(pts, d) {
  const n = pts.length;
  if (d <= 0) {
    const a = pts[n - 2], b = pts[n - 1], L = len(a, b) || 1, u = { x: (b.x - a.x) / L, y: (b.y - a.y) / L };
    return { x: b.x - u.x * d, y: b.y - u.y * d, u };
  }
  let left = d;
  for (let i = n - 1; i > 0; i--) {
    const a = pts[i - 1], b = pts[i], L = len(a, b) || 1, u = { x: (b.x - a.x) / L, y: (b.y - a.y) / L };
    if (left <= L || i === 1) return { x: b.x - u.x * left, y: b.y - u.y * left, u };
    left -= L;
  }
  return { x: pts[0].x, y: pts[0].y, u: { x: 0, y: 1 } };
}
// Offset sideways from the line: + is to the RIGHT of the direction of travel.
const side = (p, off) => ({ x: p.x + p.u.y * off, y: p.y - p.u.x * off });

// Where everything goes. road = [[lat,lng], …] in the direction of travel, ending at the work area;
// area = the work area outline. Returns items to draw (each with an id, so a dragged one can be
// put back where someone moved it) plus the labelled distances.
export function tcpLayout({ road, area, setup = 'lane_closed', sideOf = 'right', speed, roadType, laneWidth = 12, shoulderWidth = 8, noRightTurn = false }) {
  if (!Array.isArray(road) || road.length < 2) return null;
  const num = tcpNumbers({ setup, speed, roadType, laneWidth, shoulderWidth });
  const F = frame(road[road.length - 1]);
  const pts = road.map(F.to);
  const ll = p => F.back(p);
  const sgn = sideOf === 'left' ? -1 : 1;
  // The traced line is the CENTRE of the lane being closed, or the EDGE LINE beside the shoulder.
  const W = num.W;
  const outer = setup === 'shoulder' ? sgn * W : sgn * (laneWidth / 2);
  const inner = setup === 'shoulder' ? 0 : -sgn * (laneWidth / 2);
  // How long the work area runs, measured along the road (from the outline someone drew).
  let workLen = 100;
  if (Array.isArray(area) && area.length >= 3) {
    const end = along(pts, 0);
    const proj = area.map(F.to).map(q => (q.x - end.x) * end.u.x + (q.y - end.y) * end.u.y);
    workLen = Math.max(50, Math.round(Math.max(...proj) - Math.min(0, ...proj)));
  }
  const items = [];
  const cone = (d, off, id) => { const p = along(pts, d); items.push({ id, type: 'cone', ll: ll(side(p, off)) }); };
  // The taper: from the outer edge at its start down to the inner edge where the buffer begins.
  const tStart = num.buffer + num.taper;
  const nTaper = Math.max(2, Math.ceil(num.taper / num.coneTaper));
  for (let i = 0; i <= nTaper; i++) {
    const f = i / nTaper;
    cone(tStart - f * num.taper, outer + (inner - outer) * f, `taper${i}`);
  }
  // Along the buffer and the work area, then a short taper back out at the end.
  const nTan = Math.max(1, Math.ceil((num.buffer + workLen) / num.coneTangent));
  for (let i = 1; i <= nTan; i++) cone(num.buffer - (i * (num.buffer + workLen)) / nTan, inner, `tan${i}`);
  for (let i = 1; i <= 2; i++) cone(-workLen - (i * num.termination) / 2, inner + (outer - inner) * (i / 2), `term${i}`);
  // The signs, on the shoulder side beyond the closed lane / shoulder.
  const signOff = setup === 'shoulder' ? sgn * (W + 6) : sgn * (laneWidth / 2 + 6);
  const signs = signsFor(setup, sideOf);
  let at = tStart;
  signs.forEach((s, i) => {
    at += [num.A, num.B, num.C][i];
    const p = along(pts, at);
    items.push({ id: `sign${i}`, type: 'sign', code: s.code, text: s.text, ll: ll(side(p, signOff)), d: at });
  });
  { const p = along(pts, -workLen - num.termination - 50); items.push({ id: 'end', type: 'sign', code: 'G20-2', text: 'END ROAD WORK', ll: ll(side(p, signOff)) }); }
  // No Right Turn (R3-1), lane or shoulder: set on the right-hand curb where the work area starts —
  // drag it to the street or driveway that turns into the work. Regulatory, so it is not spaced
  // off the tables like the warning signs.
  const regSigns = [];
  if (noRightTurn) {
    const p = along(pts, 0);
    const off = sideOf === 'left' ? laneWidth * 1.5 + 6 : signOff;
    items.push({ id: 'nrt', type: 'sign', reg: true, code: 'R3-1', text: 'NO RIGHT TURN', ll: ll(side(p, off)) });
    regSigns.push({ code: 'R3-1', text: 'NO RIGHT TURN', where: 'at the turn into the work area' });
  }
  // An arrow board at the start of the taper on a high-speed lane closure.
  if (setup === 'lane_closed' && num.speed >= 45) { const p = along(pts, tStart); items.push({ id: 'arrow', type: 'arrow', ll: ll(side(p, outer)) }); }
  // The labelled distances, set beside the line on the open side.
  const labelAt = (d, text, id) => { const p = along(pts, d); items.push({ id, type: 'label', text, ll: ll(side(p, -sgn * (laneWidth + 14))) }); };
  labelAt(num.buffer / 2, `Buffer ${num.buffer}'`, 'lbuffer');
  labelAt(num.buffer + num.taper / 2, `${setup === 'shoulder' ? 'Shoulder taper' : 'Taper'} ${num.taper}'`, 'ltaper');
  labelAt(tStart + num.A / 2, `A ${num.A}'`, 'lA');
  labelAt(tStart + num.A + num.B / 2, `B ${num.B}'`, 'lB');
  if (signs.length > 2) labelAt(tStart + num.A + num.B + num.C / 2, `C ${num.C}'`, 'lC');
  labelAt(-workLen / 2, `Work area ${workLen}'`, 'lwork');
  // How far back up the road the layout reaches, so the person can see it fits what they traced.
  const traced = pts.slice(1).reduce((s, p, i) => s + len(pts[i], p), 0);
  const reach = at;
  return { num, items, workLen, traced: Math.round(traced), reach, signs, regSigns };
}
